import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import MaterialTable from 'material-table';
import {
    Box,
    Button,
    Chip,
    Container,
    MenuItem,
    Select,
    Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { green, grey } from '@material-ui/core/colors';
import moment from 'moment';

import {
    fixCatalogOrderCorrection,
    fixCatalogOrderCorrectionTask,
    getCatalogOrderCorrections,
} from '../../api';
import {
    CatalogOrderCorrection,
    CatalogOrderCorrectionStatus,
    CatalogOrderCorrectionTask,
    CatalogOrderCorrectionTaskStatus,
} from '../../models';
import { PaginatedResponse } from '../../../shared/models';
import { checkStatus, handleError, parseJSON } from '../../../shared/api/core/common';
import { ErrorStatusBar } from '../../../shared/components';
import {
    enqueueSnackbar,
    Toast,
    ToastError,
    ToastSuccess,
} from '../../../shared/components/Notifier';
import { EMPTY_ROW_MSG, NO_PERMISSIONS_ERROR_MSG } from '../../../shared/lib/Localization';
import { BusinessRouterPaths } from '../../../page/Routes/RouterPaths';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            padding: theme.spacing(2),
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(2),
        },
        filters: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
        },
        detail: {
            padding: theme.spacing(2),
            backgroundColor: theme.palette.background.default,
        },
        detailActions: {
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: theme.spacing(2),
        },
    }),
);

const STATUS_OPTIONS = [
    { value: CatalogOrderCorrectionStatus.PENDING, label: 'Pending' },
    { value: CatalogOrderCorrectionStatus.FIXED, label: 'Fixed' },
    { value: 'ALL', label: 'All' },
];

interface CorrectionTasksProps {
    correction: CatalogOrderCorrection;
    onUpdated: () => void;
    showToast: (message: string, options?: object) => void;
}

const CorrectionTasks = ({ correction, onUpdated, showToast }: CorrectionTasksProps) => {
    const classes = useStyles();
    const [submittingTaskId, setSubmittingTaskId] = useState<number | null>(null);
    const [isFixingOrder, setFixingOrder] = useState(false);
    const allTasksFixed =
        correction.task_count > 0
        && correction.fixed_task_count === correction.task_count;

    const markTaskFixed = (task: CatalogOrderCorrectionTask) => {
        setSubmittingTaskId(task.id);
        fixCatalogOrderCorrectionTask(task.id)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then(() => {
                setSubmittingTaskId(null);
                showToast('Correction task marked fixed.', ToastSuccess);
                onUpdated();
            })
            .catch((error: { status?: number; body?: { error?: string } }) => {
                setSubmittingTaskId(null);
                const message = error.status === 401 || error.status === 403
                    ? NO_PERMISSIONS_ERROR_MSG
                    : error.body && error.body.error
                        ? error.body.error
                        : 'Failed to mark the correction task fixed.';
                showToast(message);
            });
    };

    const markOrderFixed = () => {
        setFixingOrder(true);
        fixCatalogOrderCorrection(correction.id)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then(() => {
                setFixingOrder(false);
                showToast('Stock order marked fixed.', ToastSuccess);
                onUpdated();
            })
            .catch((error: { status?: number; body?: { error?: string } }) => {
                setFixingOrder(false);
                const message = error.status === 401 || error.status === 403
                    ? NO_PERMISSIONS_ERROR_MSG
                    : error.body && error.body.error
                        ? error.body.error
                        : 'Failed to mark the stock order fixed.';
                showToast(message);
            });
    };

    return (
        <Box className={classes.detail}>
            <MaterialTable
                columns={[
                    {
                        title: 'Temp Product',
                        field: 'source_product.name',
                        render: (task: CatalogOrderCorrectionTask) =>
                            `${task.source_product.number} - ${task.source_product.name}`,
                    },
                    {
                        title: 'Replace With',
                        field: 'replacement_product.name',
                        render: (task: CatalogOrderCorrectionTask) =>
                            `${task.replacement_product.number} - ${task.replacement_product.name}`,
                    },
                    {
                        title: 'Status',
                        field: 'status',
                        render: (task: CatalogOrderCorrectionTask) => (
                            <Chip
                                size="small"
                                label={task.status}
                                style={{
                                    color: '#fff',
                                    backgroundColor:
                                        task.status === CatalogOrderCorrectionTaskStatus.FIXED
                                            ? green[600]
                                            : grey[600],
                                }}
                            />
                        ),
                    },
                    {
                        title: 'Fixed At',
                        field: 'fixed_at',
                        render: (task: CatalogOrderCorrectionTask) =>
                            task.fixed_at ? moment(task.fixed_at).format('DD MMM YYYY HH:mm') : '—',
                    },
                ]}
                data={correction.tasks}
                options={{ toolbar: false, paging: false, search: false, sorting: false }}
                actions={[
                    (task: CatalogOrderCorrectionTask) => ({
                        icon: 'done',
                        tooltip: 'Mark Task Fixed',
                        hidden: task.status === CatalogOrderCorrectionTaskStatus.FIXED,
                        disabled: submittingTaskId === task.id,
                        onClick: () => markTaskFixed(task),
                    }),
                ]}
                localization={{ body: { emptyDataSourceMessage: EMPTY_ROW_MSG } }}
            />
            <div className={classes.detailActions}>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={
                        !allTasksFixed
                        || correction.status === CatalogOrderCorrectionStatus.FIXED
                        || isFixingOrder
                    }
                    onClick={markOrderFixed}
                >
                    Mark Stock Order Fixed
                </Button>
            </div>
        </Box>
    );
};

interface Props {
    userData: {
        selectedBusiness?: {
            business_id?: number | string;
        };
    };
    history: {
        push: (path: string) => void;
    };
    showToast: (toast: Toast) => void;
}

const CatalogOrderCorrectionTasksScreen = (props: Props) => {
    const classes = useStyles();
    const tableRef = useRef<{ onQueryChange: () => void }>();
    const [statusFilter, setStatusFilter] =
        useState<string>(CatalogOrderCorrectionStatus.PENDING);
    const [errorVisible, setErrorVisible] = useState(false);
    const businessId =
        props.userData.selectedBusiness
        && props.userData.selectedBusiness.business_id;

    const showToast = (message: string, options = ToastError) => {
        props.showToast({ message, options });
    };

    const refresh = () => {
        if (tableRef.current) {
            tableRef.current.onQueryChange();
        }
    };

    useEffect(() => {
        refresh();
    }, [businessId, statusFilter]);

    return (
        <Container maxWidth={false} className={classes.root}>
            <ErrorStatusBar isVisible={errorVisible} />
            <div className={classes.header}>
                <div>
                    <Typography variant="h6">Stock Order Correction Tasks</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Fix each listed product in Korona, then mark its task and stock order fixed.
                    </Typography>
                </div>
                <div className={classes.filters}>
                    <Select
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.target.value as string)}
                    >
                        {STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                    <Button
                        variant="outlined"
                        onClick={() => props.history.push(BusinessRouterPaths.catalogOrderReview)}
                    >
                        Temp Products
                    </Button>
                </div>
            </div>

            <MaterialTable
                tableRef={tableRef}
                columns={[
                    { title: 'Stock Order #', field: 'stock_order_number' },
                    { title: 'Location', field: 'location_name' },
                    { title: 'Supplier', field: 'supplier_name' },
                    {
                        title: 'Tasks',
                        field: 'task_count',
                        render: (correction: CatalogOrderCorrection) =>
                            `${correction.fixed_task_count} / ${correction.task_count} fixed`,
                    },
                    { title: 'Status', field: 'status' },
                    {
                        title: 'Created',
                        field: 'created_at',
                        render: (correction: CatalogOrderCorrection) =>
                            moment(correction.created_at).format('DD MMM YYYY HH:mm'),
                    },
                ]}
                data={(query) => {
                    if (!businessId) {
                        return Promise.resolve({ data: [], page: query.page, totalCount: 0 });
                    }
                    return getCatalogOrderCorrections(
                        businessId,
                        query.page + 1,
                        query.pageSize,
                        statusFilter,
                    )
                        .catch(handleError)
                        .then(checkStatus)
                        .then(parseJSON)
                        .then((response: PaginatedResponse<CatalogOrderCorrection>) => {
                            setErrorVisible(false);
                            return {
                                data: response.results,
                                page: query.page,
                                totalCount: response.count,
                            };
                        })
                        .catch(() => {
                            setErrorVisible(true);
                            return { data: [], page: query.page, totalCount: 0 };
                        });
                }}
                options={{
                    pageSize: 20,
                    pageSizeOptions: [10, 20, 50],
                    search: false,
                    sorting: false,
                    headerStyle: { backgroundColor: '#eee' },
                }}
                title="Pending Stock Orders"
                detailPanel={[
                    {
                        tooltip: 'Show correction tasks',
                        render: (correction: CatalogOrderCorrection) => (
                            <CorrectionTasks
                                correction={correction}
                                onUpdated={refresh}
                                showToast={showToast}
                            />
                        ),
                    },
                ]}
                localization={{ body: { emptyDataSourceMessage: EMPTY_ROW_MSG } }}
            />
        </Container>
    );
};

const mapStateToProps = (state: { userLocalData: Props['userData'] }) => ({
    userData: state.userLocalData,
});

const mapDispatchToProps = (dispatch: any) => ({
    showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast)),
});

export default connect(mapStateToProps, mapDispatchToProps)(
    CatalogOrderCorrectionTasksScreen,
);
